// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { WeekendHighlightDirective } from 'src/app/directives/weekend-highlight.directive';
// Restricted (c) Siemens Healthineers AG / Siemens Medical Solutions USA, 2024, All rights reserved.

import { CommonModule } from '@angular/common';
import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { APICallGateComponent } from 'src/app/modules/shared/components/api-call-gate.component';
import { NoDataIconComponent } from 'src/app/modules/shared/components/no-data-icon/no-data-icon.component';
import { LocalizedDatePipe } from 'src/app/modules/shared/pipes/localizedDate.pipe';
import { ScreenLayoutService } from 'src/app/modules/shared/services/screen-layout-service/screen-layout.service';
import { SettingsService } from 'src/app/modules/shared/services/settings.service';
import { MrqReportData } from '../../interfaces/mrq.interfaces';
import { MrqReportService } from '../../services/mrq-report/mrq-report.service';
import {
  capitalizeHeader,
  formatNumber,
} from '../../utils/mrq-table-formatting';
import { MrqMobileTableComponent } from '../mrq-mobile-table/mrq-mobile-table.component';

@Component({
  selector: 'app-mrq-table',
  templateUrl: './mrq-table.component.html',
  styleUrls: ['./mrq-table.component.scss'],
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    TranslateModule,
    LocalizedDatePipe,
    CommonModule,
    MatTableModule,
    MatSortModule,
    MatTooltipModule,
    NoDataIconComponent,
    MrqMobileTableComponent,
    WeekendHighlightDirective,
    APICallGateComponent,
  ],
})
export class MrqTableComponent implements OnInit, OnChanges {
  screenLayoutService = inject(ScreenLayoutService);
  protected settingsService = inject(SettingsService);
  protected mrqReportServive = inject(MrqReportService);

  @Input() systemSerialNumber!: string;
  @Input() systemMaterialNumber!: number;

  /**
   * Returns the apiCall endpoint for api-call-gate, similar to coilHistoryEndpoint in CoilHistoryComponent.
   */
  get mrqDashboardApi(): unknown {
    return this.mrqReportServive.getMrqDashboard;
  }

  @Input() magnetType: string = '4K';
  @Input() reportData: MrqReportData[] = [];
  @Input() loading: boolean = false;

  // Material table related properties
  matTableDataSource: MatTableDataSource<MrqReportData> =
    new MatTableDataSource<MrqReportData>([]);
  displayedColumns: string[] = [];

  @ViewChild(MatSort) matSort?: MatSort;

  // Column definitions
  columns: { id: string; name: string; tooltip?: string }[] = [];

  ngOnInit(): void {
    this.setColumnsBasedOnMagnetType();
    this.setupMatTable();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.handleInputChanges(changes);
  }

  onMatSortChange(sort: Sort): void {
    // When Material sort changes, update sorting
    if (this.matSort) {
      this.matSort.active = sort.active;
      this.matSort.direction = sort.direction;
    }
  }

  emptyValueCheck(value: unknown): string | number {
    const dateFormat = this.settingsService.dateFormat$.getValue();
    const locale = dateFormat?.shuiLocale || 'en';
    if (!value && value !== 0) {
      return '-';
    }
    if (typeof value === 'number') {
      return formatNumber(value, locale);
    }
    if (typeof value === 'string') {
      const num = Number(value);
      if (!isNaN(num)) {
        return formatNumber(num, locale);
      }
      return value;
    }
    return '-';
  }

  protected formatNumber(value: number): string {
    const dateFormat = this.settingsService.dateFormat$.getValue();
    const locale = dateFormat?.shuiLocale || 'en';
    return formatNumber(value, locale);
  }

  protected handleInputChanges(changes: SimpleChanges): void {
    if (changes['reportData'] || changes['magnetType']) {
      this.setColumnsBasedOnMagnetType();
      this.setupMatTable();
    }
  }

  protected setupMatTable(): void {
    // Set column ids as display columns for mat-table
    this.displayedColumns = this.columns.map((col) => col.id);

    // Update data source
    this.matTableDataSource = new MatTableDataSource(this.reportData);

    // Set default sort if mat-sort is available
    if (this.matSort) {
      this.matTableDataSource.sort = this.matSort;

      // Set default sort to date descending
      this.matSort.active = 'date';
      this.matSort.direction = 'desc';
    }
  }

  protected setColumnsBasedOnMagnetType(): void {
    this.columns = this.mrqReportServive.getColumnsFromReportData(
      this.reportData,
    );
  }

  protected capitalizeHeader(header: string): string {
    return capitalizeHeader(header, this.magnetType);
  }
}
